import type { Ref } from 'vue'

import { useQuery } from '@tanstack/vue-query'
import { refDebounced } from '@vueuse/core'
import { computed } from 'vue'

import type { IndexProduct } from '@/domains/products/types/models/IndexProduct.types'

import { SEARCH_SCOPES } from '@/domains/command-center/services/searchScopes'
import { MIN_SEARCH_LENGTH } from '@/domains/command-center/services/searchService'
import { isProductResult } from '@/domains/command-center/utils/searchResultUtils'
import { STANDARD_FIXED_DELAY } from '@/utils/delay-util/delayUtil'

const STOCK_SEARCH_QUERY_KEY = ['command-center', 'stock-search'] as const

/**
 * The products stock mode can adjust, for what the merchant typed.
 *
 * The palette's own Products scope, so a product found here is found the same way as anywhere else in
 * the palette. Its rows already carry every variant's stock at every location, so choosing an item
 * needs no second request.
 *
 * Stale at once: a shelf changes with every sale, and a figure from two minutes ago is the one a
 * Set would be measured against.
 */
const useStockSearch = (term: Readonly<Ref<string>>, isEnabled: Readonly<Ref<boolean>>) => {
  const productScope = SEARCH_SCOPES.find(scope => scope.id === 'product')
  // The palette's own search debounces by the same constant, so the two cannot drift apart.
  const debouncedTerm = refDebounced(term, STANDARD_FIXED_DELAY)

  const query = useQuery({
    queryKey: computed(() => [...STOCK_SEARCH_QUERY_KEY, debouncedTerm.value] as const),
    queryFn: async () => (await productScope?.search(debouncedTerm.value)) ?? [],
    enabled: computed(() => isEnabled.value && debouncedTerm.value.length >= MIN_SEARCH_LENGTH),
    staleTime: 0
  })

  const products = computed<IndexProduct[]>(() =>
    (query.data.value ?? []).filter(isProductResult).map(result => result.originalData)
  )

  /** Still typing counts as searching, so "No product matches" never flashes between keystrokes. */
  const isSearching = computed(() => query.isFetching.value || term.value !== debouncedTerm.value)

  return { products, isSearching }
}

export { STOCK_SEARCH_QUERY_KEY, useStockSearch }
